import React, { useState, useEffect } from "react";
import { FaGithub, FaLinkedin, FaBars, FaTimes, FaEnvelope } from "react-icons/fa";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";


const TypingText = React.memo(({ messages }) => {
  const [text, setText] = useState("");
  const [messageIndex, setMessageIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentMessage = messages[messageIndex];
    let timeout;

    if (!isDeleting && charIndex === currentMessage.length) {
      timeout = setTimeout(() => setIsDeleting(true), 1000);
    } else if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setMessageIndex((messageIndex + 1) % messages.length);
    } else {
      timeout = setTimeout(() => {
        setCharIndex(charIndex + (isDeleting ? -1 : 1));
        setText(
          currentMessage.substring(0, charIndex + (isDeleting ? -1 : 1))
        );
      }, isDeleting ? 30 : 100);
    }

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, messageIndex]);

  return (
    <p className="text-xl mt-4 font-mono">
      {text}
      <span className="text-blue-600 animate-pulse">|</span>
    </p>
  );
});

const SectionWrapper = React.memo(({ id, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const showUpArrow = id !== "hero";
  const showDownArrow = id !== "credentials";

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.5 }
    );
    const section = document.getElementById(id);
    if (section) observer.observe(section);
    return () => section && observer.unobserve(section);
  }, [id]);

  const getNextSection = (currentId) => {
    const ids = ["hero", "about", "skills", "experience", "credentials"];
    const index = ids.indexOf(currentId);
    return ids[(index + 1) % ids.length];
  };

  const getPreviousSection = (currentId) => {
    const ids = ["hero", "about", "skills", "experience", "credentials"];
    const index = ids.indexOf(currentId);
    return ids[(index - 1) % ids.length];
  };

  return (
    <section
      id={id}
      className="bg-gradient-to-b from-white via-slate-50 to-slate-300 pt-16 min-h-screen  flex flex-col justify-center items-center py-20 w-full px-4 text-center transition-all duration-700 ease-in-out relative"
    >
      <div className="w-full max-w-6xl">{children}</div>
      {showDownArrow && isVisible && (
        <a
          href={`#${getNextSection(id)}`}
          className="absolute right-6 top-2/3 transform translate-y-6 animate-bounce text-gray-700 text-3xl"
        >
          <IoIosArrowDown />
        </a>
      )}
      {showUpArrow && isVisible && (
        <a
          href={`#${getPreviousSection(id)}`}
          className="absolute right-6 top-1/3 transform -translate-y-14 animate-bounce text-gray-700 text-3xl"
        >
          <IoIosArrowUp />
        </a>
      )}
    </section>
  );
});

const getExperienceDuration = () => {
  const startDate = new Date("2020-12-01");
  const today = new Date();

  let years = today.getFullYear() - startDate.getFullYear();
  let months = today.getMonth() - startDate.getMonth() + 1;

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  if (months == 0) {
    return `${years} year${years !== 1 ? 's' : ''}`;
  }
  else {
    return `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''}`;
  }
};

export default function Portfolio() {
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => setMenuOpen(!menuOpen);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector("header");
      if (window.scrollY > 10) {
        header?.classList.add("shadow-lg");
      } else {
        header?.classList.remove("shadow-lg");
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const messages = [
    "Microsoft Certified Power Platform Developer",
    "Dynamics 365 CRM",
    "Tech Enthusiast",
  ];

  const aboutContent = [
    `I bring <${getExperienceDuration()}> of robust IT experience as a Software Developer, specializing in <Power Platform>, <Dynamics 365 CRM>, and reporting tools.`,
    "I possess hands-on expertise in Dynamics 365 CRM customization, adeptly utilizing tools like: <Plugins>, <Custom Workflows>, <JavaScript>, <C#>, <.NET>, Ribbon Workbench, Custom Connectors, CRM Rest Builder.",
    "I have hands-on experience integrating with: <Power Automate>, <Power BI>, <Azure Function App>, and <SharePoint>.",
    "My experience spans across <Dynamics 365 Sales>, <Marketing>, <Field Service> modules, and <Intelligent Order Management (IOM)>.",
    "I also have solid exposure to <Power Apps>, <Power Automate>, <Power Portals>, <Power BI>, and a foundational understanding of <Dynamics 365 F&O>."
  ];

  const skillData = [
    "Microsoft Dynamics CE (CRM)",
    "Power Apps",
    "Canvas Apps",
    "Model-driven Apps",
    "Power Automate",
    "Power Portals",
    "Power BI",
    "Copilot Studio",
    "Plugins",
    "JavaScript",
    "WorkFlow",
    "Business Rules",
    "Dataverse",
    "SharePoint",
    "ADF",
    "Azure Functions",
    "PCF",
    "Dynamics Sales",
    "Dynamics Customer Insights",
    "Dynamics Field Service",
    "Dynamics IOM",
    "Ribbon Workbench",

  ];

  const experienceData = [
    {
      title: "Senior Consultant 2",
      company: "EY",
      duration: "Aug 2024 - Present",
      companyLink: "https://www.linkedin.com/company/1073/",
      icon: "/ernstandyoung_logo.jpg",
      description: [
        "Built a Power Portal integrated with an external Identity Provider using Azure Functions as a proxy; implemented JWT and JWKS authentication for secure and seamless user login.",
        "Customized Dynamics 365 Field Service with Plugins, JavaScript, Business Rules, and Custom APIs to enable enhanced validations, integrations, and workflow orchestration.",
        "Automated task scheduling, reminders, and notification workflows using Power Automate flows to streamline operations.",
        "Developed a Canvas App embedded in Dynamics 365 CRM to fetch product data from F&O, calculate pricing, and optimize quote-to-order workflows.",
        "Integrated CRM and F&O using Plugins and Power Automate to synchronize key data updates like product details and payment statuses.",
        "Involved in RFP"
      ]
    },
    {
      title: "Technical Consultant II",
      company: "BASOFA",
      duration: "Feb 2023 - Aug 2024",
      companyLink: "https://www.linkedin.com/company/85822810/",
      icon: "/basofa_logo.jpg",
      description: [
        "Designed and delivered business solutions using Microsoft Power Platform, Dynamics 365 (Customer Insights, Project Operations, Intelligent Order Management), and Power Portals.",
        "Built Canvas Apps for DMF import/export, inventory, journals, and store management; integrated Power Automate with Azure APIM, configured dual write, and Dataverse.",
        "Customized OOB functionalities with Plugins, Workflows, Business Rules, JavaScript, Web API, and developed interactive PCF components using Creator Kit.",
        "Created Power Automate flows for invoice PDF generation, portal configurations (authentication, permissions, web roles), and data interactions via connectors like SharePoint, Excel, Outlook, and HTTP.",
        "Led deployments across environments, documented design/unit tests, maintained TDD, and executed end-to-end testing to ensure smooth delivery.",

        /* "Created canvas app for DMF import/export of files to D365 F&O entities using SharePoint to store the file for import job and created dynamic excel template files for report generation",
        "Worked on canvas app to easily manage the inventory and journals for the store and managing the store users",
        "Integrated Power Automate, Azure APIM with Canvas App to interact with D365 F&O environment as well as configure dual write",
        "Used Creator kit and developed PCF code components for more interactive user experience",
        "Created and configured Power Portals site form scratch which organization can manage employee's information, client's data, work related information",
        "Involved in Fit Gap analysis, design, data movement and setting up of Dynamics 365 Project Operations lite and non stocked with team.",
        "Worked on Order intake Orchestration and Order fulfillment Orchestration",
        "Led team through the project, steering with an agile methodology" */
      ]
    },
    {
      title: "Software Engineer",
      company: "Mindtree",
      duration: "Dec 2020 - Feb 2023",
      companyLink: "https://www.linkedin.com/company/4300/",
      icon: "/mindtreeltd_logo.jpg",
      description: [
        "Woked on Sales module with customisations",
        "Created PowerBI reports with RLS and without RLS using data from Data Verseand integrated inside CRM dashboard",
        "Interacted with outside organization team to collaboratively work on requirements handled across region",
        "Successfully handled and led the Production deployment as well as deployments in lower environments",
        "Received “Hats-Off” award for continued contribution to project and able to handle priority production issues",
        "Received “Master Mind” award for significant contribution in project and collaborative work with team outside organization"
      ]
    },
  ];

  const certificationData = [
     {
      name: "Microsoft Certified: GitHub Copilot",
      icon: "https://learn.microsoft.com/en-in/media/learn/certification/badges/github-copilot.svg",
      link: "https://learn.microsoft.com/en-in/users/sanathsrao-9550/credentials/95b0d09876026c42",
    },
    {
      name: "Microsoft Certified: Power Platform Developer Associate",
      icon: "https://learn.microsoft.com/media/learn/certification/badges/microsoft-certified-associate-badge.svg",
      link: "https://learn.microsoft.com/en-us/users/sanathsrao-9550/credentials/9f50fb730e0e7dfe",
    },
    {
      name: "Microsoft Certified: Dynamics 365 Fundamentals (CRM)",
      icon: "https://learn.microsoft.com/media/learn/certification/badges/microsoft-certified-fundamentals-badge.svg",
      link: "https://learn.microsoft.com/en-in/users/sanathsrao-9550/credentials/464e434659701214",
    },
    {
      name: "Microsoft Certified: Power Platform Fundamentals",
      icon: "https://learn.microsoft.com/media/learn/certification/badges/microsoft-certified-fundamentals-badge.svg",
      link: "https://learn.microsoft.com/en-in/users/sanathsrao-9550/credentials/edd61ebd904851e5",
    }
  ];

  return (
    <div className="min-h-screen font-sans text-black scroll-smooth">
      <style>{`
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(30px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }
      `}</style>

      <header className="h-16 bg-white sticky top-0 z-20 transition-shadow backdrop-blur bg-opacity-80">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          {/* <h1 className="text-3xl font-bold">Greetings</h1> */}
          <div className="md:hidden text-2xl cursor-pointer" onClick={toggleMenu}>
            {menuOpen ? <FaTimes /> : <FaBars />}
          </div>
          <nav className="space-x-6 hidden md:flex text-gray-700 font-medium">
            {["about", "skills", "experience", "credentials"].map((section) => (
              <a key={section} href={`#${section}`} className="hover:text-blue-500 capitalize">
                {section}
              </a>
            ))}
          </nav>
        </div>

        {menuOpen && (
          <div className="md:hidden px-6 pb-4 space-y-2 text-gray-700 font-medium bg-white bg-opacity-90">
            {["about", "skills", "experience", "credentials"].map((section) => (
              <a
                key={section}
                href={`#${section}`}
                onClick={closeMenu}
                className="block hover:text-blue-500 capitalize"
              >
                {section}
              </a>
            ))}
          </div>
        )}
      </header>

      <main>
        <SectionWrapper id="hero">
          <h1 className="text-5xl font-bold">Hi, I’m <span className="text-blue-600">Sanath S Rao</span></h1>
          <TypingText messages={messages} />
          <div className="flex justify-center gap-6 mt-6">
            <div className="flex items-center gap-2 text-gray-800">
              <a href="https://linkedin.com/in/sanath-s-rao" className="text-blue-600 hover:text-blue-800 transition-transform transform hover:scale-110" target="_blank" rel="noopener noreferrer">
                <FaLinkedin className="text-3xl" />
              </a>
              <span className="text-sm">sanath-s-rao</span>
            </div>
            <div className="flex items-center gap-2 text-gray-800">
              <a href="mailto:sanath00005@gmail.com" className="text-red-500 hover:text-red-700 transition-transform transform hover:scale-110" target="_blank" rel="noopener noreferrer">
                <FaEnvelope className="text-3xl" />
              </a>
              <span className="text-sm">sanath00005@gmail.com</span>
            </div>
          </div>
        </SectionWrapper>

        <SectionWrapper id="about">
          <h2 className="text-3xl font-bold mb-6">About Me</h2>
          <div className="flex flex-col md:flex-row items-center gap-8 max-w-5xl mx-auto py-20 px-4">
            <img
              src="/Sanath.JPG"
              alt="Sanath"
              className="w-56 rounded-full object-cover shadow-lg"
            />
            <div className="text-lg text-left text-gray-700 space-y-4">
              {aboutContent.map((para, idx) => {
                const formatted = para.replace(/</g, '<span class="font-semibold text-blue-600">')
                  .replace(/>/g, '</span>');
                return <p key={idx} dangerouslySetInnerHTML={{ __html: formatted }} />;
              })}

            </div>
          </div>
        </SectionWrapper>

        <SectionWrapper id="skills">
          <h2 className="text-3xl font-bold mb-6">Skills</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 text-center">
            {skillData.map((skill, idx) => (
              <div
                key={idx}
                className="bg-white/60 p-4 rounded-xl shadow text-black font-medium fade-in-up"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                {skill}
              </div>
            ))}
          </div>
        </SectionWrapper>

        <SectionWrapper id="experience">
          <h2 className="text-3xl font-bold mb-6 text-center text-gray-800">
            Professional Experience
          </h2>

          <div className="max-w-4xl mx-auto h-[500px] overflow-y-auto pr-2">
            <div className="relative border-l-4 border-blue-500 pl-6 space-y-10">
              {experienceData.map((exp, idx) => (
                <div key={idx} className="relative">
                  <a
                    href={exp.companyLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute -left-8 top-1 z-10"
                  >
                    <img
                      src={exp.icon}
                      alt={`${exp.company} logo`}
                      className="w-14 h-14 rounded-full border-2 border-white shadow object-cover bg-white"
                    />
                  </a>

                  <div className="bg-white border border-gray-200 shadow-md rounded-xl p-6 relative">
                    <div className="hidden md:block absolute top-6 right-6 text-sm text-gray-500 font-medium">
                      {exp.duration}
                    </div>

                    <h3 className="text-xl font-semibold text-gray-700">{exp.title}</h3>
                    <p className="text-sm text-indigo-600 mb-2">
                      <a
                        href={exp.companyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        {exp.company}
                      </a>
                    </p>
                    {/* Duration - inline on mobile screens */}
                    <div className="block md:hidden text-sm text-gray-500 font-medium mb-2">
                      {exp.duration}
                    </div>

                    <div className="space-y-2 text-sm text-gray-700 text-left">
                      {exp.description.map((point, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="text-blue-600 mt-1">•</span>
                          <span className="leading-relaxed">{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </SectionWrapper>

        <SectionWrapper id="credentials">
          <h2 className="text-3xl font-bold mb-6">Credentials</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {certificationData.map((cert, idx) => (
              <div
                key={idx}
                className="bg-white/70 backdrop-blur p-6 rounded-xl shadow fade-in-up text-center"
                style={{ animationDelay: `${idx * 0.2}s` }}
              >
                <img
                  src={cert.icon}
                  alt={`${cert.name} icon`}
                  className="w-20 h-20 mx-auto mb-4"
                />
                <h3 className="text-sm font-medium mb-2 text-gray-700">{cert.name}</h3>
                <a
                  href={cert.link}
                  className="text-blue-600 hover:underline inline-block mt-2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Certificate
                </a>
              </div>
            ))}
          </div>
        </SectionWrapper>

      </main>
    </div>
  );
}
